import { Box, Button, IconButton, Typography } from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CustomCellRendererProps } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileUpload as FileUploadIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { useCreateSymbol, useDeleteSymbol } from '../hooks/api/use-symbols.hook';
import { SymbolApiModel, SymbolRelationship, WithCoordinates } from '../../../types/models/symbol.model';
import { useConfirm } from '../contexts/confirm.context';
import { InfniteScrollingGrid } from '../components/AgGrid/InfiniteScrollingGrid';
import { SymbolType } from '../../../types/constants/symbol-type';
import { ArrayFilter } from '../components/AgGrid/ArrayFilter';
import { SymbolViewer } from '../components/Symbols/SymbolViewer';
import { searchSymbols } from '../api/symbols.api';

const rowHeight = 200;

export const Symbols = () => {
  const navigate = useNavigate();
  const [showConfirm] = useConfirm();

  const [createSymbol, { loading: creatingSymbol }] = useCreateSymbol();
  const [deleteSymbol, { loading: deletingSymbol, error: deleteError }] = useDeleteSymbol();

  const columns: ColDef<SymbolApiModel>[] = [
    {
      field: 'name',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
      cellStyle: { fontSize: '1.5rem', lineHeight: `${rowHeight}px` },
    },
    {
      headerName: 'Thumbnail',
      cellRenderer: ({ data }: CustomCellRendererProps<SymbolApiModel & WithCoordinates>) => (
        <SymbolViewer width={rowHeight} height={rowHeight} coordinates={data?.coordinates ?? []} showPaddedViewbox />
      ),
    },
    {
      field: 'library.name',
      colId: 'symbolLinetypeLibrary.libraryName',
      headerName: 'Library',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
      cellStyle: { fontSize: '1.5rem', lineHeight: `${rowHeight}px` },
    },
    {
      field: 'library.type',
      colId: 'symbolLinetypeLibrary.libraryType',
      headerName: 'Type',
      filter: ArrayFilter,
      sortable: true,
      filterParams: { values: Object.values(SymbolType).filter((s) => s !== SymbolType.UNKNOWN) },
      cellStyle: { fontSize: '1.5rem', lineHeight: `${rowHeight}px` },
    },
    {
      headerName: 'Actions',
      maxWidth: 200,
      cellStyle: { lineHeight: `${rowHeight}px` },
      cellRenderer: ({ api, node }: CustomCellRendererProps<SymbolApiModel & WithCoordinates>) => (
        <>
          <IconButton
            onClick={async () => {
              if (node.data) {
                await createSymbol({ ...node.data, name: `${node.data.name} Copy`, libraryId: node.data.library.id });
                api.refreshInfiniteCache();
              }
            }}
          >
            <ContentCopyIcon />
          </IconButton>
          <IconButton onClick={() => navigate(`edit/${node.id}`)}>
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              showConfirm({
                onConfirm: async () => {
                  if (node.id) {
                    await deleteSymbol({ symbolId: +node.id });
                    api.refreshInfiniteCache();
                  }
                },
                title: 'Delete Symbol',
                message: `Are you sure you want to delete symbol "${node.data?.name}"?`,
                error: deleteError?.response?.data?.message,
              });
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const getSymbols: Parameters<typeof InfniteScrollingGrid<SymbolApiModel>>[0]['onGetRows'] = async ({
    cursor,
    limit,
    filterQuery,
    sortQuery,
  }) => {
    const { data } = await searchSymbols({
      name: filterQuery?.name,
      library: filterQuery?.['library.name'],
      type: filterQuery?.['library.type'],
      include: [SymbolRelationship.COORDINATES],
      cursor,
      limit,
      sort: sortQuery,
    });
    return data;
  };

  return (
    <Box sx={{ height: '100%' }}>
      <Outlet />
      <Helmet title={'Symbols & Lines'} />
      <Box display="flex" flexDirection="column" sx={{ height: '100%', p: '0 12px' }}>
        <Box display="flex" sx={{ m: '10px 0' }}>
          <Typography variant="h5">Manage Symbols & Lines</Typography>
          <Box flexGrow={1} />
          <Button
            sx={{ mr: 2 }}
            color="primary"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`add`)}
          >
            Add
          </Button>
          <Button
            sx={{ mr: 2 }}
            color="primary"
            variant="contained"
            component="label"
            startIcon={<FileUploadIcon />}
            onClick={() => navigate(`import`)}
          >
            Import
          </Button>
          <Button
            color="primary"
            variant="contained"
            component="label"
            startIcon={<FileDownloadIcon />}
            onClick={() => navigate('export')}
          >
            Export
          </Button>
        </Box>

        <Box alignSelf={'stretch'} flexGrow={1} sx={{ pb: '12px' }}>
          <InfniteScrollingGrid
            loading={deletingSymbol || creatingSymbol}
            rowHeight={rowHeight}
            columnDefs={columns}
            onGetRows={getSymbols}
          />
        </Box>
      </Box>
    </Box>
  );
};
